import { EntityDefinition as IEntityDefinition, ISimpleEntityDefinition, LocalizedLabel } from '../../interfaces/entitydefinition';
import { Exception } from '../exceptions/Exception';
import { sendMetadataGetRequest, sendMetadataGetRequestAsync } from './MetadataApi';
import { IPromiseCache, cachedWrapper } from '@utilities/MemoryCachingHelpers';
import { IODataResponse } from '../../interfaces/general';
import { LocalizeLabel } from '@src/localization/helpers';

export class EntityDefinition {
    private static _entityDefinition: IPromiseCache<IEntityDefinition> = {};
    private static _preloadPromises: IPromiseCache<string[]> = {};

    static async preloadAsync(entityNames: string[]): Promise<string[]> {
        return cachedWrapper(entityNames.join(','), async () => {
            console.groupCollapsed("Entity Definitions Preload");
            if (!entityNames || entityNames.length === 0) {
                console.log("No entities to preload");
                console.groupEnd();
                return;
            }
            const timeStart = performance.now();
            console.log(`Preloading entity definitions for:`, entityNames);

            const metadatas = await sendMetadataGetRequestAsync(`v1.0/talxis_RetrieveEntityDefinitions(EntityNames=[${entityNames.map(x => `'${x}'`).join(',')}])`);
            if (!metadatas.ok) {
                throw new Exception('Unable to preload entity definitions!', new Exception(await metadatas.text()));
            }
            const response: IODataResponse<IEntityDefinition> = await metadatas.json();

            const entityLogicalNames: string[] = [];

            for (const entity of response.value) {
                entityLogicalNames.push(entity.LogicalName);
                this._entityDefinition[entity.LogicalName] = new Promise<IEntityDefinition>(async (resolve) => {
                    resolve(entity);
                });
            }

            console.log(`Preloading entity definitions took:`, performance.now() - timeStart);
            console.groupEnd();

            return entityLogicalNames;
        }, this._preloadPromises);
    }

    static async getAsync(): Promise<ISimpleEntityDefinition[]>;
    static async getAsync(entityName: string): Promise<IEntityDefinition>;
    static async getAsync(entityName?: string): Promise<IEntityDefinition | ISimpleEntityDefinition[]> {
        if (entityName === undefined) entityName = '*ALL*';
        return cachedWrapper(entityName, () => new Promise<IEntityDefinition | ISimpleEntityDefinition[]>(async (resolve, reject) => {
            if (Object.keys(this._preloadPromises).length > 0) {
                await Promise.all(Object.values(this._preloadPromises));
            }
            try {
                if (entityName === '*ALL*') {
                    const response = await sendMetadataGetRequestAsync(`v1.0/talxis_RetrieveEntityDefinitions(Basic=true)`);
                    if (!response.ok) {
                        throw new Error(`Entities could not be fetched!`);
                    }

                    const simpleEntitiesDefinitions = (await response.json()).value;
                    resolve(simpleEntitiesDefinitions.map((def: any) => ({
                        ...def,
                        DisplayName: LocalizeLabel(def.DisplayName as LocalizedLabel[]),
                    })));
                    return;
                }

                const response = await sendMetadataGetRequest(`v1.0/talxis_RetrieveEntityDefinitions(EntityNames=['${entityName}'])`);
                if (!response.ok) {
                    throw new Error(`Entity ${entityName} could not be fetched!`);
                }
                const entityDefinitions: IODataResponse<IEntityDefinition> = await response.json();
                resolve(entityDefinitions.value[0]);
            }
            catch (error) {
                reject(error);
            }
        }), this._entityDefinition);
    }
}